import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FaStar } from 'react-icons/fa';
import tw from 'twin.macro';
import { styled } from '@mui/material/styles';
import currencySymbol from '../../../helpers/currencySymbol';
import getHotelRateToDisplay from '../../../helpers/getHotelRateToDisplay.ts';
import getHotelRate from '../../../helpers/selectors/getHotelRate.ts';
import getHotelPriceDisplayPreference from '../../../helpers/selectors/getHotelPriceDisplayPreference';
import checkDoShowMarkupRates from '../../../helpers/selectors/checkDoShowMarkupRates';
import { TravelTypes, HotelRateDisplayPreferences } from '../../../constants';

const TooltipContainer = styled('div')`
  ${tw`absolute bg-white rounded-lg shadow-lg w-64 text-left z-30 mt-8`}
`;
const HotelName = styled('h3')`
  ${tw`font-bold text-base text-gray-800 truncate`}
`;
const Star = styled(FaStar)`
  ${tw`text-yellow-500 mr-1`}
`;

const HotelMapTooltip = ({ marker, tripId }) => {
  const pricePreference = useSelector(getHotelPriceDisplayPreference());
  const doShowMarkupRate = useSelector(checkDoShowMarkupRates({ mode: TravelTypes.hotel, tripId }));

  const rate = getHotelRateToDisplay(pricePreference,
    getHotelRate({
      doShowMarkupRate,
      rate: {
        averageNightlyRate: marker.avgNightlyRate,
        averageNightlyRateBeforeTax: marker.avgNightlyRateBeforeTax,
        currencyCode: marker.currencyCode,
        markupAverageNightlyRate: marker.markupAvgNightlyRate,
        markupAverageNightlyRateBeforeTax: marker.markupAvgNightlyRateBeforeTax,
        markupPrice: marker.markupCost,
        originalPrice: marker.cost,
      },
    }));

  const currencyCode = marker.currencyCode || 'USD';

  return (
    <TooltipContainer>
      { marker.image && <img src={marker.image} alt={marker.name} className="w-full h-32 object-cover rounded-t-lg" /> }
      <div className="p-2">
        <HotelName>{marker.name}</HotelName>
        <div className="flex items-center text-xs text-gray-600 my-1">
          {marker.starRating && Array.from({ length: 5 }).map((_, i) => {
            const filled = i < marker.starRating;
            return <Star key={`${i.toString()}`} color={filled ? '#FFC107' : '#E0E0E0'} />;
          })}
        </div>
        <div className="text-base font-bold text-gray-900 mt-1">
          { currencySymbol(currencyCode) }
          { rate.toFixed(2) }
        </div>
        {pricePreference !== HotelRateDisplayPreferences.TotalRate
          && (
          <div className="text-xs font-normal mb-1 text-gray-600">
            Per night
            {' '}
            {pricePreference === HotelRateDisplayPreferences.NightlyPrice ? 'w/' : 'w/o'}
            {' '}
            tax
          </div>
          )}
      </div>
    </TooltipContainer>
  );
};

HotelMapTooltip.propTypes = {
  marker: PropTypes.shape({
    image: PropTypes.string,
    name: PropTypes.string,
    starRating: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf([null]),
    ]),
    currencyCode: PropTypes.string,
    cost: PropTypes.number,
    avgNightlyRate: PropTypes.number,
    avgNightlyRateBeforeTax: PropTypes.number,
    markupAvgNightlyRate: PropTypes.number,
    markupAvgNightlyRateBeforeTax: PropTypes.number,
    markupCost: PropTypes.number,
  }).isRequired,
  tripId: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  lat: PropTypes.number.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  lng: PropTypes.number.isRequired,
};

HotelMapTooltip.defaultProps = {
  tripId: undefined,
};

export default HotelMapTooltip;
